import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import EditIcon from '@mui/icons-material/Edit';
import { useDispatch, useSelector } from "react-redux";
import { Box, CircularProgress, FormControl, InputLabel, Tooltip } from "@mui/material";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Popover, Select, MenuItem, Checkbox, ListItemText, IconButton, Badge} from "@mui/material";
import { toast } from "react-toastify";

import DataTablePagination from "../../../components/common/datatable/DataTablePagination";
import ButtonComponent from "../../../components/common/button/Button";
import { getContractHideColumnData } from '../../../redux/actions/HideCDColumn/GetContractHideColumns';
import { getContractHildeColumnsEndpoint, postContractHildeColumnsEndpoint } from '../../../common/endpoints';
import { postContractHideColumnData } from "../../../redux/actions/HideCDColumn/PostContractHideColumns";
import { Loader } from "../../../components/common/Loader";
import { updateContractsData } from "../../../redux/actions/Contracts";


const DataDetails = (props) => {
  const dispatch = useDispatch();
  const contractsDataList = useSelector((state) => state?.getContractsData);
  const [columnFilterDropdownOption, setColumnFilterDropdownOption] = useState([]);
  const [filteredColumnsData, setFilteredColumnsData] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [badgeCount, setBadgeCount] = useState();
  const [anchorEl, setAnchorEl] = useState(null);
  const userEmail = localStorage.getItem("data");
  const [disableFilterBtn, setDisableFilterBtn] = useState(true);
  const stateSelector = useSelector((state) => state?.postContractHidedColumn);
  const fetchContractReports = useSelector((state) => state?.getContractsData);
  const [filteredTotalItems, setFilteredTotalItems] = useState(fetchContractReports?.data?.totalNumberOfItems);
  const [filteredTotalColumn, setFilteredTotalColumn] = useState(0);
  const [isGridReady, setIsGridReady] = useState(false);
  const [columnState, setColumnState] = useState(null);
  const [selectedColumn, setSelectedColumn] = useState('');
  const [masterPdtSource, setMasterPdtSource] = useState('');
  
  const handlePostFilterChanged = (filteredCount, totalCount) => {
    setFilteredTotalItems(filteredCount); // Update the filtered total items
  };

  const plantId = localStorage.getItem("selectedCDPlantCode");

  const handleClickHide = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseHide = () => {
    setAnchorEl(null);
  };

  // add helpers near the top (after imports)
  // const parseDateDDMMYYYY = (v) => {
  //   if (!v || typeof v !== "string") return null;
  //   const parts = v.split("-");
  //   if (parts.length !== 3) return null;
  //   const d = new Date(+parts[2], +parts[1] - 1, +parts[0]);
  //   return isNaN(d.getTime()) ? null : d;
  // };
  // const formatDateDDMMYYYY = (d) => {
  //   if (!(d instanceof Date) || isNaN(d.getTime())) return "-";
  //   const dd = String(d.getDate()).padStart(2, "0");
  //   const mm = String(d.getMonth() + 1).padStart(2, "0");
  //   const yyyy = d.getFullYear();
  //   return `${dd}-${mm}-${yyyy}`;
  // };

  const handleColumnChange = (event) => {
    const value = event.target.value;
    if (value.includes("selectAll")) {
      if (selectedColumns.length === columnFilterDropdownOption.length) {
        setSelectedColumns([]);
      } else {
        setSelectedColumns(columnFilterDropdownOption.map((col) => col.headerName));
      }
    } else {
      setSelectedColumns(value);
    }
  };

  const renderDropdownValue = (selected) => {
    if (selected?.length === 0) return "Select Columns";
    if (selected?.length === 1)
      return columnFilterDropdownOption.find(
        (col) => col?.headerName === selected?.[0]
      )?.headerName;

    const firstColumnName = columnFilterDropdownOption.find(
      (col) => col?.headerName === selected?.[0]
    )?.headerName;
    return `${firstColumnName}, +${selected?.length - 1}`;
  };

  const getOptionalFields = (headerItems) => {
    const optionalFields = headerItems.flatMap((item) => {
      if (item.children) {
        return item.children.filter((child) => child.optionalField === true);
      } else if (item.optionalField === true) {
        return [item];
      }
      return [];
    });
    return optionalFields;
  };

  useEffect(() => {
    const allRows = contractsDataList?.data?.contractDataOutputList || [];
    // Only update if any row needs to be updated
    const needsUpdate = allRows.some(row =>
      row.pdtManual === undefined ||
      row.pdtManual === null ||
      row.pdtManual === "" ||
      row.pdtManual === "-"
    );
    if (needsUpdate) {
      const updatedRows = allRows.map(row => {
        const manufacturingLeadTime = parseFloat(row.manufacturingLeadTime) || 0;
        const transitTime = parseFloat(row.transitTime) || 0;
        const iqd = parseFloat(row.iqd) || 0;
        const automationPDT = manufacturingLeadTime + transitTime + iqd;
        if (
          row.pdtManual === undefined ||
          row.pdtManual === null ||
          row.pdtManual === "" ||
          row.pdtManual === "-"
        ) {
          return { ...row, pdtManual: automationPDT };
        }
        return row;
      });

      // Update Redux
      dispatch(updateContractsData(updatedRows));

      // Update selected rows in parent if needed
      if (props?.rowsData) {
        const updatedSelectedRows = updatedRows.filter(row =>
          props.rowsData.some(sel => sel.contractSeqId === row.contractSeqId)
        );
        props.setRowsData(updatedSelectedRows);
      }
    }
  }, [contractsDataList?.data?.contractDataOutputList]);

  const headerItems = useMemo(() => {
    return [
      {
        headerName: "Contract Serial#",
        field: "contractSerialNumber",
        sortable: true,
        resizable: false,
        pinned: "left",
        unSortIcon: true,
        cellClassRules: {
          "bold-cell": (params) => params.data.isNew === true,
        },
      },
      {
        headerName: "Header Level Data Fields",
        headerClass: "center-header",
        children: [
          {
            headerName: "Vendor Number*",
            field: "vendorNumber",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              "red-cell": (params) => isInvalidValue(params.value),
            },
            optionalField: true,
            // cellClass: "cpsComments-cell",
            // editable: (params) =>
            //   params.node.data.status.toLowerCase() !==
            //   "contract submitted successfully",
            // onCellValueChanged: (params) =>
            //   props.handleCellValueChange(params, "vendorNumber"),
            // valueSetter: (params) =>
            //   props.handleCellValueChange(params, "vendorNumber"),
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, vendorNumber: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}
                  </span>
                  {/* <EditIcon fontSize="sm" /> */}
                </div>
              );
            },
          },
          {
            headerName: "Vendor Name*",
            field: "vendorName",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            // initialWidth: 180,
            // wrapText: true,
            // autoHeight: true,
            cellClass: "paymentTerms-cell",
            // cellClassRules: {
            //   "red-cell": (params) => isInvalidValue(params.value),
            // },
            optionalField: true,
            // editable: (params) =>
            //   params.node.data.status.toLowerCase() !==
            //   "contract submitted successfully",
            // onCellValueChanged: (params) =>
            //   props.handleCellValueChange(params, "vendorName"),
            // valueSetter: (params) =>
            //   props.handleCellValueChange(params, "vendorName"),
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, vendorName: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}
                  </span>
                  {/* <EditIcon fontSize="sm" /> */}
                </div>
              );
            },
          },
          {
            headerName: "Agreement Type*",
            field: "agreementType",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              "red-cell": (params) => isInvalidValue(params.value),
            },
            editable: (params) =>
              params.node.data.status.toLowerCase() === "contract submitted successfully" ||
              props.userRole === "Viewer" 
              ? false : true,
            cellClass: props.userRole === "Viewer" ? "" : "cpsComments-cell",
            cellEditor: "agTextCellEditor",
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, agreementType: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              const isEditable =
                params.node.data.status.toLowerCase() !==
                  "contract submitted successfully";
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  {isEditable || props.userRole === "Viewer" ? null : <EditIcon fontSize="sm" />}
                </div>
              );
            },
            optionalField: true,
          },
          {
            headerName: "Agreement Date*",
            field: "agreementDate",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              "red-cell": (params) => isInvalidValue(params.value),
            },
            optionalField: true,

            // return a JS Date for AG Grid (used for sorting & filtering)
            valueGetter: (params) => {
              const v = params.data?.agreementDate;
              if (!v) return null;
              // expecting "DD-MM-YYYY"
              const parts = v.split("-");
              if (parts.length !== 3) return null;
              const day = parseInt(parts[0], 10);
              const month = parseInt(parts[1], 10) - 1;
              const year = parseInt(parts[2], 10);
              const d = new Date(year, month, day);
              // if invalid date return null
              return isNaN(d.getTime()) ? null : d;
            },

            // show original string (or format the Date if you prefer)
            valueFormatter: (params) => {
              // prefer original string if present
              if (params.data?.agreementDate) return params.data.agreementDate;
              if (params.value instanceof Date && !isNaN(params.value)) {
                const dd = String(params.value.getDate()).padStart(2, "0");
                const mm = String(params.value.getMonth() + 1).padStart(2, "0");
                const yyyy = params.value.getFullYear();
                return `${dd}-${mm}-${yyyy}`;
              }
              return "-";
            },

            filter: "agDateColumnFilter",
            filterParams: {
              // AG Grid passes filterLocalDateAtMidnight (Date) and cellValue (our Date from valueGetter OR original string)
              comparator: (filterLocalDateAtMidnight, cellValue) => {
                if (!cellValue) return -1;
                let cellDate;
                if (cellValue instanceof Date) {
                  cellDate = cellValue;
                } else if (typeof cellValue === "string") {
                  const parts = cellValue.split("-");
                  if (parts.length !== 3) return -1;
                  const day = parseInt(parts[0], 10);
                  const month = parseInt(parts[1], 10) - 1;
                  const year = parseInt(parts[2], 10);
                  cellDate = new Date(year, month, day);
                  if (isNaN(cellDate.getTime())) return -1;
                } else {
                  return -1;
                }

                // compare only date portion (ignore time)
                const cellYMD = new Date(cellDate.getFullYear(), cellDate.getMonth(), cellDate.getDate());
                const filterYMD = new Date(
                  filterLocalDateAtMidnight.getFullYear(),
                  filterLocalDateAtMidnight.getMonth(),
                  filterLocalDateAtMidnight.getDate()
                );

                if (cellYMD.getTime() === filterYMD.getTime()) return 0;
                return cellYMD < filterYMD ? -1 : 1;
              },
            },

            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, agreementDate: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.valueFormatted || params.value || params.data?.agreementDate || "-"}
                  </span>
                  {/* {params.node.data.isNew && <EditIcon fontSize="sm" />} */}
                </div>
              );
            },
          },
          {
            headerName: "Agreement Number*",
            field: "agreementNumber",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            optionalField: true,
            cellClass: props.userRole === "Viewer" ? "" : "cpsComments-cell",
            editable: (params) =>
              params.node.data.status.toLowerCase() ===  "contract submitted successfully" ||
              props.userRole === "Viewer"
              ? false : true,
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, agreementNumber: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
          },
          {
            headerName: "Company Code*",
            field: "companyCode",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            editable: (params) => params.node.data.status.toLowerCase() !== "contract submitted successfully" && 
              !params.node.data.agreementNumber && props.userRole !== "Viewer",
            cellClass: (params) =>
              !params.node.data.agreementNumber && props.userRole !=="Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agTextCellEditor",
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, companyCode: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              const isEditable = params.node.data.status.toLowerCase() !== "contract submitted successfully" && 
                !params.node.data.agreementNumber && props.userRole !== "Viewer";
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  {isEditable && <EditIcon fontSize="sm" />}
                </div>
              );
            },
            cellClassRules: {
              "red-cell": (params) => isInvalidValue(params.value),
            },
            optionalField: true,
          },
          {
            headerName: "Purchasing organization*",
            field: "purchaseOrganisation",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            editable: (params) => !params.node.data.agreementNumber && 
              params.node.data.status.toLowerCase() !== "contract submitted successfully" && 
              props.userRole !== "Viewer",
            cellClass: (params) => !params.node.data.agreementNumber && props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agTextCellEditor",
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, purchaseOrganisation: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              const isEditable = !params.node.data.agreementNumber && 
                params.node.data.status.toLowerCase() !== "contract submitted successfully" &&
                props.userRole !== "Viewer";
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  {isEditable && <EditIcon fontSize="sm" />}
                </div>
              );
            },
            cellClassRules: {
              "red-cell": (params) => isInvalidValue(params.value),
            },
            optionalField: true,
          },
          {
            headerName: "Purchasing Group*",
            field: "purchaseGroup",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            editable: (params) => params.node.data.status.toLowerCase() === "contract submitted successfully" ||
              props.userRole === "Viewer"
              ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agTextCellEditor",
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, purchaseGroup: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              const isEditable = params.node.data.status.toLowerCase() !== "contract submitted successfully";
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  {(isEditable && props.userRole !== "Viewer") && <EditIcon fontSize="sm" />}
                </div>
              );
            },
            cellClassRules: {
              "red-cell": (params) => isInvalidValue(params.value),
            },
            optionalField: true,
          },
          // {
          //   headerName: "Validity Start Date*",
          //   field: "validityStartDate",
          //   sortable: true,
          //   resizable: false,
          //   unSortIcon: true,
          //   editable: (params) =>
          //     params.node.data.status.toLowerCase() !==
          //     "contract submitted successfully",
          //   cellClass: "cpsComments-cell",
          //   cellEditor: "agTextCellEditor", // allow manual DD-MM-YYYY input

          //   // provide Date object to AG Grid for sorting/filtering
          //   // valueGetter: (params) => {
          //   //   const v = params.data?.validityStartDate;
          //   //   return parseDateDDMMYYYY(v);
          //   // },
          //   // display original string if present, otherwise formatted valueGetter
          //   valueFormatter: (params) => {
          //     if (params.data?.validityStartDate) return params.data.validityStartDate;
          //     if (params.value instanceof Date && !isNaN(params.value)) return formatDateDDMMYYYY(params.value);
          //     return "-";
          //   },

          //   // use a sort comparator that compares parsed dates (keeps cell value as string)
          //   sortComparator: (valueA, valueB, nodeA, nodeB) => {
          //     const aStr = nodeA?.data?.validityStartDate ?? valueA;
          //     const bStr = nodeB?.data?.validityStartDate ?? valueB;
          //     const dA = parseDateDDMMYYYY(aStr);
          //     const dB = parseDateDDMMYYYY(bStr);
          //     if (!dA && !dB) return 0;
          //     if (!dA) return -1;
          //     if (!dB) return 1;
          //     return dA.getTime() - dB.getTime();
          //   },

          //   filter: "agDateColumnFilter",
          //   filterParams: {
          //     comparator: (filterLocalDateAtMidnight, cellValue, node) => {
          //       // prefer the source string from the node data (keeps consistent parsing)
          //       const source = node?.data?.validityStartDate ?? cellValue;
          //       if (!source) return -1;
          //       const cellDate = typeof source === "string" ? parseDateDDMMYYYY(source) : source instanceof Date ? source : null;
          //       if (!cellDate) return -1;
          //       const cellYMD = new Date(cellDate.getFullYear(), cellDate.getMonth(), cellDate.getDate());
          //       const filterYMD = new Date(
          //         filterLocalDateAtMidnight.getFullYear(),
          //         filterLocalDateAtMidnight.getMonth(),
          //         filterLocalDateAtMidnight.getDate()
          //       );
          //       if (cellYMD.getTime() === filterYMD.getTime()) return 0;
          //       return cellYMD.getTime() < filterYMD.getTime() ? -1 : 1;
          //     },
          //     browserDatePicker: true,
          //   },
          //   // accept typed string or Date (if you wire a date editor later)
          //   valueSetter: (params) => {
          //     const newVal = params.newValue;
          //     if (newVal === params.oldValue) return false;
          //     if (newVal instanceof Date && !isNaN(newVal)) {
          //       params.node.setData({ ...params.data, validityStartDate: formatDateDDMMYYYY(newVal) });
          //       return true;
          //     }
          //     if (typeof newVal === "string") {
          //       const parsed = parseDateDDMMYYYY(newVal);
          //       if (parsed) {
          //         params.node.setData({ ...params.data, validityStartDate: formatDateDDMMYYYY(parsed) });
          //         return true;
          //       }
          //       // store raw string if user entered a custom format
          //       params.node.setData({ ...params.data, validityStartDate: newVal });
          //       return true;
          //     }
          //     return false;
          //   },

          //   cellRenderer: (params) => {
          //     return (
          //       <div className="cps-comments-icon">
          //         <span style={{ flex: 1, padding: "0px 4px" }}>
          //           {params.valueFormatted || params.value || params.data?.validityStartDate || "-"}
          //         </span>
          //         <EditIcon fontSize="sm" />
          //       </div>
          //     );
          //   },
          //   cellClassRules: {
          //     "red-cell": (params) => isInvalidValue(params.value || params.data?.validityStartDate),
          //   },
          //   optionalField: true,
          // },
          {
            headerName: "Validity Start Date*",
            field: "validityStartDate",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agTextCellEditor",
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, validityStartDate: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
            cellClassRules: {
              "red-cell": (params) => isInvalidValue(params.value),
            },
            optionalField: true,
          },
          {
            headerName: "Validity End Date*",
            field: "validityEndDate",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agTextCellEditor",
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, validityEndDate: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
            cellClassRules: {
              "red-cell": (params) => isInvalidValue(params.value),
            },
            optionalField: true,
          },
          {
            headerName: "Terms of Payment key*",
            field: "paymentTerms",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agSelectCellEditor",
            cellEditorParams: (params) => {
              const apiPaymentTerms = params?.node?.data?.paymentTerms;
              const defaultValues = ["N030", "N060", "N090", "N120"];
              if (apiPaymentTerms && !defaultValues.includes(apiPaymentTerms)) {
                return { values: [...defaultValues, apiPaymentTerms] };
              }
              return { values: defaultValues };
            },
            // onCellValueChanged: (params) =>
            //   props.handleCellValueChange(params, "paymentTerms"),
            // valueSetter: (params) =>
            //   props.handleCellValueChange(params, "paymentTerms"),
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, paymentTerms: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  {props.userRole !== "Viewer" && <ArrowDropDownIcon />}
                </div>
              );
            },
            cellClassRules: {
              "red-cell": (params) => isInvalidValue(params.value),
            },
            optionalField: true,
          },
          {
            headerName: "Target value for Header Area/Dist*",
            field: "targetValue",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agTextCellEditor",
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, targetValue: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
            cellClassRules: {
              "red-cell": (params) => {
                const value = params?.value;
                const isEmptyValue =
                  value === null ||
                  value === undefined ||
                  value === "" ||
                  value === 0 ||
                  value === "-"; // Check for empty values
                return params?.data?.agreementType === "WK" && isEmptyValue;
              },
            },
            optionalField: true,
          },
          {
            headerName: "Currency Key*",
            field: "currency",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            headerClass: "bef-PDT",
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agTextCellEditor",
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, currency: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
            cellClassRules: {
              "red-cell": (params) => isInvalidValue(params.value),
            },
            optionalField: true,
          },
          {
            headerName: "Header Incoterms Version*",
            field: "headIncotermVersion",
            sortable: true,
            resizable: false,
            headerClass: "aft-PDT",
            unSortIcon: true,
            // editable: (params) =>
            //   params.node.data.status.toLowerCase() !==
            //   "contract submitted successfully",
            // cellClass: "cpsComments-cell",
            // cellEditor: "agTextCellEditor",
            // onCellValueChanged: (params) =>
            //   props.handleCellValueChange(params, "headIncotermVersion"),
            // valueSetter: (params) =>
            //   props.handleCellValueChange(params, "headIncotermVersion"),
            // cellRenderer: (params) => {
            //   return (
            //     <div className="cps-comments-icon">
            //       <span style={{ flex: 1, padding: "0px 4px" }}>
            //         {params.value}{" "}
            //       </span>
            //       <EditIcon fontSize="sm" />
            //     </div>
            //   );
            // },
            cellClassRules: {
              "red-cell": (params) => isInvalidValue(params.value),
            },
            optionalField: true,
          },
          {
            headerName: "Incoterms 1*",
            field: "headIncoterms",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
               ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agTextCellEditor",
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, headIncoterms: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
            cellClassRules: {
              "red-cell": (params) => isInvalidValue(params.value),
            },
            optionalField: true,
          },
          {
            headerName: "Header Incoterms Location 1*",
            field: "headIncotermsLoc1",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
               ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agTextCellEditor",
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, headIncotermsLoc1: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
            cellClassRules: {
              "red-cell": (params) => isInvalidValue(params.value),
            },
            optionalField: true,
          },
          {
            headerName: "Header Incoterms Location 2",
            field: "headIncotermsLoc2",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
               ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agTextCellEditor",
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, headIncotermsLoc2: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
            // cellClassRules: {
            //   "red-cell": (params) => {
            //     const value = params?.value;
            //     const isEmptyValue =
            //       value === null ||
            //       value === undefined ||
            //       value === "" ||
            //       value === 0 ||
            //       value === "-";
            //     return params?.data?.confControl === "ZKO" && isEmptyValue;
            //   },
            // },
            optionalField: true,
          },
          {
            headerName: "Different Invoicing Party",
            field: "invoicingParty",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
               ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agTextCellEditor",
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, invoicingParty: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
            cellClassRules: {
              "red-cell": (params) => isInvalidValue(params.value),
            },
            optionalField: true,
          },
          {
            headerName: "Goods Supplier",
            field: "supplyingVendor",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
               ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agTextCellEditor",
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, supplyingVendor: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
            optionalField: true,
          },
          {
            headerName: "Category*",
            field: "category",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
               ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agTextCellEditor",
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, category: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
            cellClassRules: {
              "red-cell": (params) => isInvalidValue(params.value),
            },
            optionalField: true,
          },
        ],
      },
      {
        headerName: "Planned Delivery Time",
        headerClass: "center-header",
        children: [
          {
            headerName: "Manufacturing LT",
            field: "manufacturingLeadTime",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
               ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agTextCellEditor",
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, manufacturingLeadTime: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
            optionalField: true,
          },
          {
            headerName: "IQD",
            field: "iqd",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClass: props.userRole !== "Viewer" ? "small-cell cpsComments-cell" : "",
            optionalField: true,
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
               ? false : true,
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, iqd: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
          },
          {
            headerName: "PDT - Contract Automation",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClass: "small-cell",
            valueGetter: (params) => {
              const manufacturingLeadTime =
                parseFloat(params.data.manufacturingLeadTime) || 0;
              const transitTime = parseFloat(params.data.transitTime) || 0;
              const iqd = parseFloat(params.data.iqd) || 0;

              const totalPDT = manufacturingLeadTime + transitTime + iqd;

              return totalPDT;
            },
            cellRenderer: (params) => {
              const manufacturingLeadTime =
                parseFloat(params.data.manufacturingLeadTime) || 0;
              const transitTime = parseFloat(params.data.transitTime) || 0;
              const iqd = parseFloat(params.data.iqd) || 0;

              const totalPDT = manufacturingLeadTime + transitTime + iqd;

              return totalPDT;
            },
            optionalField: true,
          },
          {
            headerName: "PDT - SAP",
            field: "pdtSap",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            optionalField: true, 
          },
          {
            headerName: "PDT - LT Recommended",
            field: "pdtLtRecommended",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            optionalField: true,
          },
          {
            headerName: "Manual PDT",
            field: "pdtManual",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            editable: props.userRole !== "Viewer" ? true : false,
            cellClass: props.userRole !== "Viewer" ? "small-cell cpsComments-cell" : "",
            cellEditor: "agTextCellEditor",
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, pdtManual: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
            optionalField: true,
          },
          {
            headerName: "PDT Source",
            field: "selectedPdtSource",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agSelectCellEditor",
            cellEditorParams: {
              values: ["", "CONTRACT AUTOMATION", "SAP", "LT RECOMMENDED", "MANUAL PDT"],
            },
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, selectedPdtSource: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  {props.userRole !== "Viewer" && <ArrowDropDownIcon />}
                </div>
              );
            },
            optionalField: true,
          },
        ],
      },
      {
        headerName: "Item Level Data Fields",
        headerClass: "center-header",
        children: [
          {
            headerName: "Item Number of Purchasing Doc*",
            field: "itemPurchasingDocItemNo",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            optionalField: true,
            cellClass: "cpsComments-cell",
            cellEditor: "agTextCellEditor",
            editable: (params) => params.node.data.status.toLowerCase() !== "contract submitted successfully",
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, itemPurchasingDocItemNo: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}
                  </span>
                  <EditIcon fontSize="sm" />
                </div>
              );
            },
          },
          {
            headerName: "Deletion Indicator in Purchasing doc",
            field: "itemDeletionIndicator",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agSelectCellEditor",
            cellEditorParams: {
              values: ["X", ""],
            },
            // onCellValueChanged: (params) =>
            //   props.handleCellValueChange(params, "itemDeletionIndicator"),
            // valueSetter: (params) =>
            //   props.handleCellValueChange(params, "itemDeletionIndicator"),
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, itemDeletionIndicator: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  {props.userRole !== "Viewer" && <ArrowDropDownIcon />}
                </div>
              );
            },
            optionalField: true,
          },
          {
            headerName: "Item Category in Purchasing Doc",
            field: "itemPurchasingDocItemCategory",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            // editable: (params) =>
            //   params.node.data.isNew && params.node.data.status.toLowerCase() !==
            //   "contract submitted successfully",
            // cellClass: (params) => params.node.data.isNew ? "cpsComments-cell" : "",
            cellEditor: "agTextCellEditor",
            // onCellValueChanged: (params) =>
            //   props.handleCellValueChange(
            //     params,
            //     "itemPurchasingDocItemCategory"
            //   ),
            // valueSetter: (params) =>
            //   props.handleCellValueChange(
            //     params,
            //     "itemPurchasingDocItemCategory"
            //   ),
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, itemPurchasingDocItemCategory: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  {/* {params.node.data.isNew && <EditIcon fontSize="sm" />} */}
                </div>
              );
            },
            optionalField: true,
          },
          {
            headerName: "Account Assignment",
            field: "itemAccountAssignmentCategory",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agTextCellEditor",
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, itemAccountAssignmentCategory: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
            optionalField: true,
          },
          {
            headerName: "Material Number*",
            field: "itemMaterialNumber",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              "red-cell": (params) => isInvalidValue(params.value),
            },
            optionalField: true,
            // cellClass: (params) => params.node.data.isNew ? "cpsComments-cell" : "",
            // editable: (params) =>
            //   params.node.data.isNew && params.node.data.status.toLowerCase() !==
            //   "contract submitted successfully",
            // onCellValueChanged: (params) =>
            //   props.handleCellValueChange(params, "itemMaterialNumber"),
            // valueSetter: (params) =>
            //   props.handleCellValueChange(params, "itemMaterialNumber"),
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, itemMaterialNumber: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}
                  </span>
                  {/* {params.node.data.isNew && <EditIcon fontSize="sm" />} */}
                </div>
              );
            },
          },
          {
            headerName: "Short Text",
            field: "itemMaterialShortDesc",
            sortable: true,
            resizable: false,
            initialWidth: 180,
            wrapText: true,
            autoHeight: true,
            cellClass: "paymentTerms-cell",
            unSortIcon: true,
            optionalField: true,
          },
          {
            headerName: "Target Quantity*",
            field: "itemTargetQuantity",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agTextCellEditor",
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, itemTargetQuantity: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              const change = (params.data?.changes || []).find((c) => c.field === "itemTargetQuantity");
              const tooltipText = `Contract Value : ${change?.contractValue ?? ""}, PriceMemo Value : ${change?.pmValue ?? ""}`;
              const isEditable = params.node.data.status.toLowerCase() === "contract submitted successfully" || props.userRole === "Viewer"
                ? false : true;
              return (
                change ? (
                  <Tooltip title={tooltipText} placement="top" arrow>
                    <div className="cps-comments-icon" style={{ display: "flex", alignItems: "center", }}>
                      <span style={{ flex: 1, padding: "0px 4px" }}>{params.value ?? "-"}</span>
                      {isEditable && <EditIcon fontSize="small" />}
                    </div>
                  </Tooltip>
                ) :
                  <div className="cps-comments-icon" style={{ display: "flex", alignItems: "center", }}>
                    <span style={{ flex: 1, padding: "0px 4px" }}>{params.value ?? "-"}</span>
                    {isEditable && <EditIcon fontSize="small" />}
                  </div>
              );
            },
            cellClassRules: {
              "red-cell": (params) => {
                const value = params?.value;
                const isEmptyValue =
                  value === null ||
                  value === undefined ||
                  value === "" ||
                  value === 0 ||
                  value === "-"; // Check for empty values
                return params?.data?.agreementType === "MK" && isEmptyValue;
                // const value =
                //   params?.data?.agreementType === "MK" &&
                //   params?.value?.length === 0;
                // return value;
              },
              "bold-cell": (params) =>
                Array.isArray(params.data?.changes) &&
                params.data.changes.some((c) => c.field === "itemTargetQuantity"),
            },
            optionalField: true,
          },
          {
            headerName: "Order Unit",
            field: "itemOrderUnit",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            optionalField: true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, itemOrderUnit: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
          },
          {
            headerName: "Net Price*",
            field: "itemNetPrice",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agTextCellEditor",
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, itemNetPrice: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              const change = (params.data?.changes || []).find((c) => c.field === "itemNetPrice");
              const tooltipText = `Contract Value : ${change?.contractValue ?? ""}, PriceMemo Value : ${change?.pmValue ?? ""}`;
              const isEditable = params.node.data.status.toLowerCase() === "contract submitted successfully" || props.userRole === "Viewer"
                ? false : true;
              return (
                change ?
                (
                  <Tooltip title={tooltipText} placement="top" arrow>
                    <div className="cps-comments-icon" style={{ display: "flex", alignItems: "center", }}>
                      <span style={{ flex: 1, padding: "0px 4px" }}>{params.value ?? "-"}</span>
                      {isEditable && <EditIcon fontSize="small" />}
                    </div>
                  </Tooltip>
                ) :
                <div className="cps-comments-icon" style={{ display: "flex", alignItems: "center", }}>
                  <span style={{ flex: 1, padding: "0px 4px" }}>{params.value ?? "-"}</span>
                  {isEditable && <EditIcon fontSize="small" />}
                </div>
              );
            },
            cellClassRules: {
              "red-cell": (params) => {
                let value = params.value;
                if (value !== null && value !== undefined) {
                  value = value.toString().trim();
                }
                return value === "" || value === "-" || value === "0";
              },
              "bold-cell": (params) =>
                Array.isArray(params.data?.changes) &&
                params.data.changes.some((c) => c.field === "itemNetPrice"),
            },
            optionalField: true,
          },
          {
            headerName: "Per*",
            field: "itemPer",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agTextCellEditor",
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, itemPer: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
            cellClassRules: {
              "red-cell": (params) => isInvalidValue(params.value),
            },
            optionalField: true,
          },
          {
            headerName: "Order Price Unit*",
            field: "itemOrderPriceUnit",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agTextCellEditor",
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, itemOrderPriceUnit: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
            cellClassRules: {
              "red-cell": (params) => isInvalidValue(params.value),
            },
            optionalField: true,
          },
          {
            headerName: "Material Group",
            field: "itemMaterialGroup",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            optionalField: true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, itemMaterialGroup: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
          },
          {
            headerName: "Plant*",
            field: "itemPlant",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              "red-cell": (params) => isInvalidValue(params.value),
            },
            optionalField: true,
            // cellClass: (params) => params.node.data.isNew ? "cpsComments-cell" : "",
            // editable: (params) =>
            //   params.node.data.isNew && params.node.data.status.toLowerCase() !==
            //   "contract submitted successfully",
            // onCellValueChanged: (params) =>
            //   props.handleCellValueChange(params, "itemPlant"),
            // valueSetter: (params) =>
            //   props.handleCellValueChange(params, "itemPlant"),
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, itemPlant: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}
                  </span>
                  {/* {params.node.data.isNew && <EditIcon fontSize="sm" />} */}
                </div>
              );
            },
          },
          {
            headerName: "Storage Location*",
            field: "itemStorageLocation",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agTextCellEditor",
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, itemStorageLocation: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
            cellClassRules: {
              "red-cell": (params) => isInvalidValue(params.value),
            },
            optionalField: true,
          },
          {
            headerName: "Order Ack Requirement*",
            field: "itemOrderAckRequirement",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClassRules: {
              "red-cell": (params) => isInvalidValue(params.value),
            },
            optionalField: true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, itemOrderAckRequirement: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
          },
          {
            headerName: "Material Number Used by Supplier*",
            field: "itemSupplierMaterialNumber",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agTextCellEditor",
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, itemSupplierMaterialNumber: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              const change = (params.data?.changes || []).find((c) => c.field === "itemSupplierMaterialNumber");
              const tooltipText = `Contract Value : ${change?.contractValue ?? ""}, PriceMemo Value : ${change?.pmValue ?? ""}`;
              const isEditable = params.node.data.status.toLowerCase() === "contract submitted successfully" || props.userRole === "Viewer"
                ? false
                : true;
              return (
                change ? 
                (
                  <Tooltip title={tooltipText} placement="top" arrow>
                    <div className="cps-comments-icon" style={{ display: "flex", alignItems: "center", }}>
                      <span style={{ flex: 1, padding: "0px 4px" }}>{params.value ?? "-"}</span>
                      {isEditable && <EditIcon fontSize="small" />}
                    </div>
                  </Tooltip>
                ) :
                <div className="cps-comments-icon" style={{ display: "flex", alignItems: "center", }}>
                  <span style={{ flex: 1, padding: "0px 4px" }}>{params.value ?? "-"}</span>
                  {isEditable && <EditIcon fontSize="small" />}
                </div>
              );
            },
            cellClassRules: {
              "bold-cell": (params) =>
                Array.isArray(params.data?.changes) &&
                params.data.changes.some((c) => c.field === "itemSupplierMaterialNumber"),
            },
            optionalField: true,
          },
          {
            headerName: "Incoterms Version*",
            field: "itemIncotermVersion",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            // editable: (params) =>
            //   params.node.data.status.toLowerCase() !==
            //   "contract submitted successfully",
            // cellClass: "cpsComments-cell",
            // cellEditor: "agTextCellEditor",
            // onCellValueChanged: (params) =>
            //   props.handleCellValueChange(params, "itemIncotermVersion"),
            // valueSetter: (params) =>
            //   props.handleCellValueChange(params, "itemIncotermVersion"),
            // cellRenderer: (params) => {
            //   return (
            //     <div className="cps-comments-icon">
            //       <span style={{ flex: 1, padding: "0px 4px" }}>
            //         {params.value}{" "}
            //       </span>
            //       <EditIcon fontSize="sm" />
            //     </div>
            //   );
            // },
            cellClassRules: {
              "red-cell": (params) => isInvalidValue(params.value),
            },
            optionalField: true,
          },
          {
            headerName: "Incoterms*",
            field: "itemIncoterms",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agTextCellEditor",
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, itemIncoterms: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              const change = (params.data?.changes || []).find((c) => c.field === "itemIncoterms");
              const tooltipText = `Contract Value : ${change?.contractValue ?? ""}, PriceMemo Value : ${change?.pmValue ?? ""}`;
              const isEditable = params.node.data.status.toLowerCase() === "contract submitted successfully" || props.userRole === "Viewer"
                ? false
                : true;
              return (
                change ?
                (
                  <Tooltip title={tooltipText} placement="top" arrow>
                    <div className="cps-comments-icon" style={{ display: "flex", alignItems: "center", }}>
                      <span style={{ flex: 1, padding: "0px 4px" }}>{params.value ?? "-"}</span>
                      {isEditable && <EditIcon fontSize="small" />}
                    </div>
                  </Tooltip>
                ) :
                <div className="cps-comments-icon" style={{ display: "flex", alignItems: "center", }}>
                  <span style={{ flex: 1, padding: "0px 4px" }}>{params.value ?? "-"}</span>
                  {isEditable && <EditIcon fontSize="small" />}
                </div>
              );
            },
            cellClassRules: {
              "red-cell": (params) => isInvalidValue(params.value),
              "bold-cell": (params) =>
                Array.isArray(params.data?.changes) &&
                params.data.changes.some((c) => c.field === "itemIncoterms"),
            },
            optionalField: true,
          },
          {
            headerName: "Incoterms Location 1*",
            field: "itemIncotermsLoc1",
            sortable: true,
            resizable: false,
            optionalField: true,
            unSortIcon: true,
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agTextCellEditor",
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, itemIncotermsLoc1: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              const change = (params.data?.changes || []).find((c) => c.field === "itemIncotermsLoc1");
              const tooltipText = `Contract Value : ${change?.contractValue ?? ""}, PriceMemo Value : ${change?.pmValue ?? ""}`;
              const isEditable = params.node.data.status.toLowerCase() === "contract submitted successfully" || props.userRole === "Viewer"
                ? false
                : true;
              return (
                change ?
                (
                  <Tooltip title={tooltipText} placement="top" arrow>
                      <div className="cps-comments-icon" style={{ display: "flex", alignItems: "center", }}>
                        <span style={{ flex: 1, padding: "0px 4px" }}>{params.value ?? "-"}</span>
                        {isEditable && <EditIcon fontSize="small" />}
                      </div>
                  </Tooltip>
                ) :
                  <div className="cps-comments-icon" style={{ display: "flex", alignItems: "center", }}>
                    <span style={{ flex: 1, padding: "0px 4px" }}>{params.value ?? "-"}</span>
                    {isEditable && <EditIcon fontSize="small" />}
                  </div>
              );
            },
            cellClassRules: {
              "red-cell": (params) => isInvalidValue(params.value),
              "bold-cell": (params) =>
                Array.isArray(params.data?.changes) &&
                params.data.changes.some((c) => c.field === "itemIncotermsLoc1"),
            },
          },
          {
            headerName: "Incoterms Location 2",
            field: "itemIncotermsLoc2",
            sortable: true,
            resizable: false,
            optionalField: true,
            unSortIcon: true,
            editable: (params) =>
              params.node.data.status.toLowerCase() === "contract submitted successfully" || props.userRole === "Viewer"
                ? false
                : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agTextCellEditor",
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, itemIncotermsLoc2: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
            cellClassRules: {
              "red-cell": (params) => {
                const value = params?.value;
                const isEmptyValue =
                  value === null ||
                  value === undefined ||
                  value === "" ||
                  value === 0 ||
                  value === "-";
                return params?.data?.confControl === "ZKO" && isEmptyValue;
              },
            },
          },
        ],
      },
      {
        headerName: "Pricing Data Fields",
        headerClass: "center-header",
        children: [
          {
            headerName: "Condition Type",
            field: "conditionType",
            sortable: true,
            resizable: false,
            optionalField: true,
            unSortIcon: true,
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agTextCellEditor",
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, conditionType: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
            // cellClassRules: {
            //   'red-cell': (params) => {
            //     const value = params.value ? params.value.toString().trim() : '';
            //     return !value;
            //   }
            // }
          },
          {
            headerName: "Condition Amount",
            field: "conditionAmount",
            sortable: true,
            optionalField: true,
            resizable: false,
            unSortIcon: true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, conditionAmount: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
            cellClassRules: {
              "red-cell": (params) => isInvalidValue(params.value),
            },
          },
          {
            headerName: "Per 1",
            field: "pricingPer",
            sortable: true,
            resizable: false,
            optionalField: true,
            unSortIcon: true,
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agTextCellEditor",
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, pricingPer: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
            cellClassRules: {
              "red-cell": (params) => isInvalidValue(params.value),
            },
          },
          {
            headerName: "Order Price Unit",
            field: "pricingOrderPriceUnit",
            sortable: true,
            optionalField: true,
            resizable: false,
            unSortIcon: true,
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agTextCellEditor",
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, pricingOrderPriceUnit: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
            cellClassRules: {
              "red-cell": (params) => isInvalidValue(params.value),
            },
          },
          {
            headerName: "Valid From",
            field: "validFrom",
            sortable: true,
            optionalField: true,
            resizable: false,
            unSortIcon: true,
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agTextCellEditor",
            cellEditorParams: {
              placeholder: "DD-MM-YYYY",
            },
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, validFrom: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value || "DD-MM-YYYY"}{" "}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
            // cellClassRules: {
            //   'red-cell': (params) => {
            //     const value = params.value ? params.value.toString().trim() : '';
            //     return !value;
            //   }
            // }
          },
          {
            headerName: "Valid To",
            field: "validTo",
            sortable: true,
            optionalField: true,
            resizable: false,
            unSortIcon: true,
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agTextCellEditor",
            cellEditorParams: {
              placeholder: "DD-MM-YYYY",
            },
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, validTo: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value || "DD-MM-YYYY"}{" "}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
            // cellClassRules: {
            //   'red-cell': (params) => {
            //     const value = params.value ? params.value.toString().trim() : '';
            //     return !value;
            //   }
            // }
          },
          {
            headerName: "Scale Type",
            field: "scaleType",
            sortable: true,
            resizable: false,
            optionalField: true,
            unSortIcon: true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, scaleType: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
          },
          {
            headerName: "Condition Scale Quantity",
            field: "scaleQuantity",
            sortable: true,
            resizable: false,
            optionalField: true,
            unSortIcon: true,
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agTextCellEditor",
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, scaleQuantity: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
            // cellClassRules: {
            //   'red-cell': (params) => {
            //     const value = params.value ? params.value.toString().trim() : '';
            //     return !value;
            //   }
            // }
          },
          {
            headerName: "Condition Scale Unit of Measure",
            field: "scaleUOM",
            sortable: true,
            resizable: false,
            optionalField: true,
            unSortIcon: true,
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agTextCellEditor",
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, scaleUOM: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
            cellClassRules: {
              'red-cell': (params) => {
                const scaleQuantity = params.data.scaleQuantity;
                const scaleUOM = params.value;
                if (scaleQuantity !== null && scaleQuantity !== "" && scaleQuantity !== "-") {
                  return !scaleUOM || scaleUOM === "" || scaleUOM === "-" || scaleUOM === null;
                }
                return false;
                // return scaleQuantity && (!scaleUOM || scaleUOM === "" || scaleUOM === "-" || scaleUOM === null);
              }
            }
          },
          {
            headerName: "Scale Amount",
            field: "scaleAmount",
            sortable: true,
            resizable: false,
            optionalField: true,
            unSortIcon: true,
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agTextCellEditor",
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, scaleAmount: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              const change = (params.data?.changes || []).find((c) => c.field === "scaleAmount");
              const tooltipText =  `Contract Value : ${change?.contractValue ?? ""}, PriceMemo Value : ${change?.pmValue ?? ""}`;
              const isEditable = params.node.data.status.toLowerCase() === "contract submitted successfully" || props.userRole === "Viewer"
                ? false
                : true;
              const highlightStyle = change ? { backgroundColor: "#fff7e6" } : undefined; // pale highlight
              return (
                change ? 
                (
                  <Tooltip title={tooltipText} placement="top" arrow>
                    <div className="cps-comments-icon" style={{ display: "flex", alignItems: "center", ...highlightStyle }}>
                      <span style={{ flex: 1, padding: "0px 4px" }}>{params.value ?? "-"}</span>
                      {isEditable && <EditIcon fontSize="small" />}
                    </div>
                  </Tooltip>
                ) :
                <div className="cps-comments-icon" style={{ display: "flex", alignItems: "center", }}>
                  <span style={{ flex: 1, padding: "0px 4px" }}>{params.value ?? "-"}</span>
                  {isEditable && <EditIcon fontSize="small" />}
                </div>
              );
            },
            cellClassRules: {
              "bold-cell": (params) =>
                Array.isArray(params.data?.changes) &&
                params.data.changes.some((c) => c.field === "scaleAmount"),
            },
          },
          {
            headerName: "Per 2",
            field: "pricingPer2",
            sortable: true,
            resizable: false,
            optionalField: true,
            unSortIcon: true,
            // editable: (params) =>
            //   params.node.data.status.toLowerCase() !==
            //   "contract submitted successfully",
            // cellClass: "cpsComments-cell",
            cellEditor: "agTextCellEditor",
            // onCellValueChanged: (params) =>
            //   props.handleCellValueChange(params, "pricingPer2"),
            // valueSetter: (params) =>
            //   props.handleCellValueChange(params, "pricingPer2"),
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, pricingPer2: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            // cellRenderer: (params) => {
            //   return (
            //     <div className="cps-comments-icon">
            //       <span style={{ flex: 1, padding: "0px 4px" }}>
            //         {params.value}{" "}
            //       </span>
            //       <EditIcon fontSize="sm" />
            //     </div>
            //   );
            // },
            // cellClassRules: {
            //   'red-cell': (params) => {
            //   const value = params.value ? params.value.toString().trim() : '';
            //   return !value;
            //   }
            // }
          },
        ],
      },
      {
        headerName: "Other Details",
        headerClass: "center-header",
        children: [
          {
            headerName: "Prior Supplier",
            field: "priorSupplier",
            sortable: true,
            resizable: false,
            optionalField: true,
            unSortIcon: true,
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agTextCellEditor",
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, priorSupplier: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
            // cellClassRules: {
            //   "red-cell": (params) => {
            //     const value = params.value ? params.value.toString().trim() : "";
            //     return !value;
            //   },
            // },
          },
          {
            headerName: "Confirmation Control Key*",
            field: "confControl",
            sortable: true,
            resizable: false,
            optionalField: true,
            unSortIcon: true,
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agTextCellEditor",
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, confControl: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
            cellClassRules: {
              "red-cell": (params) => isInvalidValue(params.value),
            },
          },
          {
            headerName: "Tax Code*",
            field: "taxCode",
            sortable: true,
            resizable: false,
            optionalField: true,
            unSortIcon: true,
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agTextCellEditor",
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, taxCode: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
            cellClassRules: {
              // "red-cell": (params) => isInvalidValue(params.value) || plantId !== '5040' || plantId !== '0178' || plantId !== '0340' || plantId !== '0194',
              "red-cell": (params) => {
                const isInvalid = isInvalidValue(params.value);
                const isOptionalPlant = [
                  "5040",
                  "0178",
                  "0340",
                  "0194",
                  "0120"
                ].includes(plantId);
                return isInvalid && !isOptionalPlant;
              },
            },
          },
          {
            headerName: "Shipping Instructions*",
            field: "shippingInstructions",
            sortable: true,
            resizable: false,
            optionalField: true,
            unSortIcon: true,
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agSelectCellEditor",
            cellEditorParams: (params) => {
              const shipDrop = params.data.shippingDrop;
              if(shipDrop) {
                return {
                  values: shipDrop?.fieldOptions?.map((item, index) => `${index}||${item.ddKey}`),
                }
              }
            },
            valueSetter: (params) => {
              const [indexStr, ddKey] = params.newValue.split("||");
              const index = parseInt(indexStr, 10);
              const fieldOptions = params.data.shippingDrop?.fieldOptions;

              if (!fieldOptions || isNaN(index) || !fieldOptions[index]) return false;

              const selectedOption = fieldOptions[index];

              // Optional: avoid redundant update
              if (
                params.oldValue === params.newValue &&
                params.data.transitTime === selectedOption.ddKey2
              ) {
                return false;
              }

              const dataCopy = {
                ...params.data,
                shippingInstructions: ddKey,
                transitTime: selectedOption.ddKey2,
              };

              params.node.setData(dataCopy);
              return true;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon" style={{backgroundColor:params.value==="V1 (Shipping Instruction 1)"?"#e3b91e":'none'}}>
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  {props.userRole !== "Viewer" && <ArrowDropDownIcon />}
                </div>
              );
            },
            // cellClassRules: {
            //   "red-cell": (params) => isInvalidValue(params.value),
            // },
          },
          {
            headerName: "Transit Time",
            field: "transitTime",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellEditor: "agTextCellEditor",
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                </div>
              );
            },
            optionalField: true,
          },
          {
            headerName: "Pricing Cond. Currency",
            field: "pricingCondCurrency",
            sortable: true,
            resizable: false,
            optionalField: true,
            unSortIcon: true,
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agTextCellEditor",
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, pricingCondCurrency: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
          },
          // {
          //   headerName: "Open PO",
          //   field: "openPo",
          //   sortable: true,
          //   resizable: false,
          //   optionalField: true,
          //   filterParams: {
          //     filterOptions: ["contains", "startsWith", "endsWith"],
          //     defaultOption: "contains",
          //   },
          //   filter: "agTextColumnFilter",
          //   suppressMovable: false,
          //   unSortIcon: true,
          //   editable: (params) =>
          //     params.node.data.status.toLowerCase() !==
          //     "contract submitted successfully",
          //   cellClass: "cpsComments-cell",
          //   cellEditor: "agTextCellEditor",
          //   onCellValueChanged: (params) =>
          //     props.handleCellValueChange(params, "openPo"),
          //   valueSetter: (params) => props.handleCellValueChange(params, "openPo"),
          //   cellRenderer: (params) => {
          //     return (
          //       <div className="cps-comments-icon">
          //         <span style={{ flex: 1, padding: "0px 4px" }}>
          //           {params.value}{" "}
          //         </span>
          //         <EditIcon fontSize="sm" />
          //       </div>
          //     );
          //   },
          // },
          {
            headerName: "Forecast",
            field: "forecast",
            sortable: true,
            resizable: false,
            optionalField: true,
            unSortIcon: true,
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agTextCellEditor",
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, forecastFile: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
          },
          {
            headerName: "Payment in Days",
            field: "paymentInDays",
            sortable: true,
            resizable: false,
            optionalField: true,
            unSortIcon: true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, paymentInDays: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
          },
          {
            headerName: "GR time",
            field: "goodsReceiptTime",
            sortable: true,
            resizable: false,
            optionalField: true,
            unSortIcon: true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, goodsReceiptTime: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
          },
          {
            headerName: "Control key for QM",
            field: "controlKeyQM",
            sortable: true,
            resizable: false,
            optionalField: true,
            unSortIcon: true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, controlKeyQM: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
          },
          {
            headerName: "Gross weight",
            field: "grossWeight",
            sortable: true,
            resizable: false,
            optionalField: true,
            unSortIcon: true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, grossWeight: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
          },
          {
            headerName: "Net weight",
            field: "netWeight",
            sortable: true,
            resizable: false,
            optionalField: true,
            unSortIcon: true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, netWeight: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
          },
          {
            headerName: "Weight Unit",
            field: "weightUnit",
            sortable: true,
            resizable: false,
            optionalField: true,
            unSortIcon: true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, weightUnit: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
          },
          {
            headerName: "Volume",
            field: "volume",
            sortable: true,
            resizable: false,
            optionalField: true,
            unSortIcon: true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, volume: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
          },
          {
            headerName: "Volume unit",
            field: "volumeUnit",
            sortable: true,
            resizable: false,
            optionalField: true,
            unSortIcon: true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, volumeUnit: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
          },
          {
            headerName: "Underdelivery Tolerance",
            field: "underDeliveryTolerance",
            sortable: true,
            resizable: false,
            optionalField: true,
            unSortIcon: true,
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agTextCellEditor",
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, underDeliveryTolerance: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
          },
          {
            headerName: "Overdelivery Tolerance",
            field: "overDeliveryTolerance",
            sortable: true,
            resizable: false,
            optionalField: true,
            unSortIcon: true,
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agTextCellEditor",
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, overDeliveryTolerance: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
          },
          {
            headerName: "Unlimited Overdelivery",
            field: "unlimitedOverDeliveryAllowed",
            sortable: true,
            resizable: false,
            optionalField: true,
            unSortIcon: true,
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agTextCellEditor",
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, unlimitedOverDeliveryAllowed: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
          },
          {
            headerName: "Min. Rem. self life",
            field: "minRemainingShelfLife",
            sortable: true,
            resizable: false,
            optionalField: true,
            unSortIcon: true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, minRemainingShelfLife: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
          },
          {
            headerName: "ERS",
            field: "ers",
            sortable: true,
            resizable: false,
            optionalField: true,
            unSortIcon: true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, ers: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
          },
          {
            headerName: "GR-based invoice verif.",
            field: "grInvoiceVeri",
            sortable: true,
            resizable: false,
            optionalField: true,
            unSortIcon: true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, grInvoiceVeri: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
          },
          {
            headerName: "GR ",
            field: "gr",
            sortable: true,
            resizable: false,
            optionalField: true,
            unSortIcon: true,
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agSelectCellEditor",
            cellEditorParams: {
              values: ["X", ""],
            },
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, gr: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  {props.userRole !== "Viewer" && <ArrowDropDownIcon />}
                </div>
              );
            },
          },
          {
            headerName: "IR",
            field: "ir",
            sortable: true,
            resizable: false,
            optionalField: true,
            unSortIcon: true,
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agSelectCellEditor",
            cellEditorParams: {
              values: ["X", ""],
            },
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, ir: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  {props.userRole !== "Viewer" && <ArrowDropDownIcon />}
                </div>
              );
            },
          },
          {
            headerName: "Our Reference",
            field: "ourReference",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            editable: props.userRole === "Viewer" ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agTextCellEditor",
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, ourReference: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
          },
          {
            headerName: "Your Reference",
            field: "yourReference",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            editable: props.userRole === "Viewer" ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agTextCellEditor",
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, yourReference: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
          },
        ],
      },
      {
        headerName: "Header Text",
        headerClass: "center-header",
        children: [
          {
            headerName: "Text 1",
            field: "headerText1",
            sortable: true,
            resizable: false,
            optionalField: true,
            unSortIcon: true,
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agTextCellEditor",
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, headerText1: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
          },
          {
            headerName: "Text 2",
            field: "headerText2",
            sortable: true,
            resizable: false,
            optionalField: true,
            unSortIcon: true,
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agTextCellEditor",
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, headerText2: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
          },
        ],
      },
      {
        headerName: "Header Notes",
        headerClass: "center-header",
        children: [
          {
            headerName: "Note 1",
            field: "headerNote1",
            sortable: true,
            resizable: false,
            optionalField: true,
            unSortIcon: true,
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agTextCellEditor",
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, headerNote1: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
          },
          {
            headerName: "Note 2",
            field: "headerNote2",
            sortable: true,
            resizable: false,
            optionalField: true,
            unSortIcon: true,
            editable: (params) =>
              params.node.data.status.toLowerCase() ===
              "contract submitted successfully" || props.userRole === "Viewer"
              ? false : true,
            cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
            cellEditor: "agTextCellEditor",
            valueSetter: (params) => {
              if (params.newValue !== params.oldValue) {
                const dataCopy = { ...params.data, headerNote2: params.newValue };
                params.node.setData(dataCopy);
                return true;
              }
              return false;
            },
            cellRenderer: (params) => {
              return (
                <div className="cps-comments-icon">
                  <span style={{ flex: 1, padding: "0px 4px" }}>
                    {params.value}{" "}
                  </span>
                  {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                </div>
              );
            },
          },
        ],
      },
      {
        headerName: "Delivery Address",
        field: "deliveryAddress",
        sortable: true,
        resizable: false,
        optionalField: true,
        unSortIcon: true,
        editable: props.userRole === "Viewer" ? false : true,
        cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
        cellEditor: "agTextCellEditor",
        valueSetter: (params) => {
          if (params.newValue !== params.oldValue) {
            const dataCopy = { ...params.data, deliveryAddress: params.newValue };
            params.node.setData(dataCopy);
            return true;
          }
          return false;
        },
        cellRenderer: (params) => {
          return (
            <div className="cps-comments-icon">
              <span style={{ flex: 1, padding: "0px 4px" }}>
                {params.value}{" "}
              </span>
              {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
            </div>
          );
        },
      },
      {
        headerName: "Customer Number",
        field: "customerNumber",
        sortable: true,
        resizable: false,
        optionalField: true,
        unSortIcon: true,
        editable: props.userRole === "Viewer" ? false : true,
        cellClass: props.userRole !== "Viewer" ? "cpsComments-cell" : "",
        cellEditor: "agTextCellEditor",
        valueSetter: (params) => {
          if (params.newValue !== params.oldValue) {
            const dataCopy = { ...params.data, customerNumber: params.newValue };
            params.node.setData(dataCopy);
            return true;
          }
          return false;
        },
        cellRenderer: (params) => {
          return (
            <div className="cps-comments-icon">
              <span style={{ flex: 1, padding: "0px 4px" }}>
                {params.value}{" "}
              </span>
              {props.userRole !== "Viewer" && <EditIcon fontSize="sm" />}
            </div>
          );
        },
      },
      {
        headerName: "Error Details",
        field: "errorDetails",
        sortable: true,
        resizable: true,
        optionalField: true,
        unSortIcon: true,
        initialWidth: 180,
        wrapText: true,
        autoHeight: true,
        cellClass: "status-cell",
        cellRenderer: (params) => {
          const maxLength = 50; // Maximum length before truncating
          const text = params.value || ""; // The error details text
          const isLongText = text.length > maxLength;

          if (!params.node.showFullText) {
            params.node.showFullText = false;
          }

          const toggleText = () => {
            params.node.showFullText = !params.node.showFullText;
            params.api.refreshCells({ rowNodes: [params.node], force: true }); // Refresh the cell to update the UI
          };

          return isLongText ? (
            <div>
              {params.node.showFullText ? (
                <>
                  <span>{text}</span>{" "}
                  <button
                    onClick={toggleText}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#181d1f",
                      cursor: "pointer",
                      textDecoration: "underline",
                      fontWeight: "bold",
                    }}
                  >
                    Less
                  </button>
                </>
              ) : (
                <>
                  <span>{text.substring(0, maxLength)}...</span>{" "}
                  <button
                    onClick={toggleText}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#181d1f",
                      cursor: "pointer",
                      textDecoration: "underline",
                      fontWeight: "bold",
                    }}
                  >
                    More
                  </button>
                </>
              )}
            </div>
          ) : (
            <span>{text}</span>
          );
        },
      },
      {
        headerName: "Status",
        field: "status",
        sortable: true,
        optionalField: true,
        resizable: false,
        initialWidth: 180,
        wrapText: true,
        autoHeight: true,
        cellClass: "paymentTerms-cell",
        unSortIcon: true,
      },
      {
        headerName: "",
        pinned: "right",
        sortable: false,
        width: 120,
        resizable: false,
        unSortIcon: true,
        headerComponent: () => (
          <div style={{ textAlign: "center" }}>
            <span>Scroll to</span>
            <br />
            <span>Errored Column</span>
          </div>
        ),
        cellRenderer: (params) => {
          const mandatoryFields = [
            "agreementDate",
            "companyCode",
            "purchaseOrganisation",
            "purchaseGroup",
            "validityStartDate",
            "validityEndDate",
            "paymentTerms",
            "currency",
            "headIncotermVersion",
            "headIncoterms",
            "headIncotermsLoc1",
            "category",
            "itemMaterialNumber",
            "itemOrderPriceUnit",
            "pricingOrderPriceUnit",
            "itemNetPrice",
            "itemPer",
            "itemPlant",
            "itemStorageLocation",
            "itemOrderAckRequirement",
            "itemIncotermVersion",
            "itemIncoterms",
            "itemIncotermsLoc1",
            "confControl",
            // "shippingInstructions",
            "invoicingParty",
            "pricingPer",
            "conditionAmount"
          ];
          const rowData = params.data;
          if (
            rowData.agreementType === "WK" &&
            (rowData?.targetValue === null ||
              rowData?.targetValue === undefined ||
              rowData?.targetValue === "" ||
              rowData?.targetValue === 0 ||
              rowData?.targetValue === "-")
          ) {
            mandatoryFields.push("targetValue");
          } else if (
            rowData.agreementType === "MK" &&
            (rowData?.itemTargetQuantity === null ||
              rowData?.itemTargetQuantity === undefined ||
              rowData?.itemTargetQuantity === "" ||
              rowData?.itemTargetQuantity === 0 ||
              rowData?.itemTargetQuantity === "-")
          ) {
            mandatoryFields.push("itemTargetQuantity");
          }
          if (
            rowData?.confControl === "ZKO" &&
            (
              // !rowData?.headIncotermsLoc2 ||
              // rowData?.headIncotermsLoc2 === "-" ||
              // rowData?.headIncotermsLoc2 === "" ||
              // rowData?.headIncotermsLoc2 === 0 ||
              !rowData?.itemIncotermsLoc2 ||
              rowData?.itemIncotermsLoc2 === "-" ||
              rowData?.itemIncotermsLoc2 === 0 ||
              rowData?.itemIncotermsLoc2 === "")
          ) {
            mandatoryFields.push("itemIncotermsLoc2");
          }
          if (!["5040", "0178", "0340", "0194", "0120"].includes(plantId)) {
            mandatoryFields.push("taxCode");
          }
          if (
            rowData?.scaleQuantity !== null &&
            rowData?.scaleQuantity !== "" &&
            rowData?.scaleQuantity !== "-"
          ) {
            mandatoryFields.push("scaleUOM");
          }
          const isInvalidNetPrice = (val) => {
            if (val === null || val === undefined) return true;
            const str = val.toString().trim();
            return str === "" || str === "-" || str === "0";
          };
          // Check if all mandatory fields are filled
          const isDisabled = mandatoryFields.every(
            (field) => {
              if (field === "itemNetPrice") {
                return !isInvalidNetPrice(rowData[field]);
              }
              return rowData[field] && rowData[field] !== "-";
            }
          );

          return (
            <ButtonComponent
              maxWidth="100px"
              label="Next"
              bgColor="black"
              color="white"
              endIcon={<WarningAmberIcon style={{ color: "red !important" }} />}
              disabled={isDisabled}
              onClick={() => {
                // Find the first empty mandatory field
                const firstEmptyField = mandatoryFields.find(
                  (field) => !rowData[field] || rowData[field] === "-"
                );
                if (firstEmptyField) {
                  // Scroll to the column of the first empty field
                  params.api.ensureColumnVisible(firstEmptyField);
                }
              }}
            />
          );
        },
      },
    ];
  }, [props.data, plantId])

  const getContractHideColumnsData = useCallback(async () => {
    try {
      const responseHideContractData = await dispatch(
        getContractHideColumnData({
          dynamicEndpoint: `${getContractHildeColumnsEndpoint}/${userEmail}`,
          params: {
            plant: localStorage.getItem("selectedLTPlantCode"),
          },
        })
      );
      if (responseHideContractData?.meta?.requestStatus === "fulfilled") {
        setSelectedColumns(responseHideContractData?.payload);
        setBadgeCount(responseHideContractData?.payload?.length);

        const hideDropdownOptions = getOptionalFields(headerItems)
        setColumnFilterDropdownOption(hideDropdownOptions);

        const displayedColumns = headerItems.flatMap((col) => {
          if (col.children) {
            const filteredChildren = col.children.filter(
              (child) =>
                !child.optionalField ||
                !responseHideContractData?.payload?.includes(child.headerName)
            );
  
            if (filteredChildren.length > 0) {
              return [{ ...col, children: filteredChildren }];
            }
            return [];
          } else {
            return !col.optionalField ||
              !responseHideContractData?.payload?.includes(col.headerName)
              ? [col]
              : [];
          }
        });

        setFilteredColumnsData([...displayedColumns]);
      } else {
        console.error("Failed to fetch data:", responseHideContractData?.error);
        toast.error(responseHideContractData?.payload?.errorMessage, {
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error("API error:", error);
      toast.error(error, {
        autoClose: 2000,
      });
    }
  }, [dispatch, headerItems, userEmail]);

  useEffect(() => {
    getContractHideColumnsData();
  }, [props?.procurementDivisionType, getContractHideColumnsData]);

  const handleSave = async () => {
    const responseBody = {
      "userEmail": userEmail,
      "hiddenColumns": selectedColumns
    }

    try {
      const responsePostContractHideColumns = await dispatch(postContractHideColumnData({
        dynamicEndpoint: `${postContractHildeColumnsEndpoint}`,
        data: responseBody,
        params: {
          plant: localStorage.getItem("selectedLTPlantCode")
        }
      }));
      if (responsePostContractHideColumns?.meta?.requestStatus === 'fulfilled') {
        getContractHideColumnsData();
        handleCloseHide();
      } else {
        console.error('Failed to fetch data:', responsePostContractHideColumns?.error);
      }
    } catch (error) {
      console.error('API error:', error);
    }

    const displayedColumns = headerItems.flatMap((col) => {
      if (col.children) {
        const filteredChildren = col.children.filter(
          (child) =>
            !child.optionalField || !selectedColumns.includes(child.headerName)
        );
        if (filteredChildren.length > 0) {
          return [{ ...col, children: filteredChildren }];
        }
        return [];
      } else {
        return !col.optionalField || !selectedColumns.includes(col.headerName)
          ? [col]
          : [];
      }
    });

    setFilteredColumnsData([...displayedColumns]);
  };

  const isInvalidValue = (value) => {
    return value === null || value === undefined || value === "" || value === "-";
  };

  const selectionColumnDef = useMemo(() => ({
    pinned: "left",
  }), []);

  const gridApiRef = useRef(null);

  const onColumnChange = e => {
    const col = e.target.value;
    setSelectedColumn(col);
    if (col && gridApiRef.current) {
      gridApiRef.current.ensureColumnVisible(col);
    }
  };
    
  const onGridReady = (params) => {
    gridApiRef.current = params.api;
    setIsGridReady(true);
    if (columnState) {
      params.columnApi.applyColumnState({ state: columnState, applyOrder: true });
    }
  };

  useEffect(() => {
    // Restore filter model from localStorage on component mount
    if (isGridReady && gridApiRef?.current) {
      const savedFilterModel = localStorage.getItem("contractFilterModel");
      if (savedFilterModel) {
        const parsedFilterModel = JSON.parse(savedFilterModel);

        gridApiRef.current.setFilterModel(parsedFilterModel);
        setDisableFilterBtn(true); // Enable the "Clear All Filters" button
      }
    }
  }, [isGridReady]);

  const clearAllFilters = () => {
    if (gridApiRef.current) {
      gridApiRef.current.setFilterModel(null);
      localStorage.removeItem("contractFilterModel");
      setDisableFilterBtn(true); 
    }
  };

  const onFilterChanged = (params) => {
    if (gridApiRef.current) {
      const filterModel = gridApiRef.current.getFilterModel();
      setDisableFilterBtn(Object.keys(filterModel).length === 0);
      setFilteredTotalColumn(Object.keys(filterModel).length);

      localStorage.setItem("contractFilterModel", JSON.stringify(filterModel));

      // Get the filtered and total row counts
      const filteredRowCount = params.api.getDisplayedRowCount();
      setFilteredTotalItems(filteredRowCount);
    }
  };  

  const onColumnMoved = useCallback((params) => {
    if (params && params.columnApi && typeof params.columnApi.getColumnState === "function") {
      setColumnState(params.columnApi.getColumnState());
    }
  }, []);

  const onPdtChange = (event) => {
    const selectedValue = event.target.value;
    setMasterPdtSource(selectedValue);

    // Get the latest grid data (with user edits)
    let latestRows = [];
    if (gridApiRef.current) {
      latestRows = [];
      gridApiRef.current.forEachNode(node => {
        latestRows.push({ ...node.data });
      });
    } else {
      // fallback to redux if gridApi is not ready
      latestRows = contractsDataList?.data?.contractDataOutputList || [];
    }

    // Update selectedPdtSource for selected rows, keep other fields as-is
    const updatedRows = latestRows.map(row => {
      if (props.rowsData?.some(sel => sel.contractSeqId === row.contractSeqId)) {
        return { ...row, selectedPdtSource: selectedValue };
      }
      return row;
    });

    dispatch(updateContractsData(updatedRows));

    // Update selected rows in parent
    if (props?.rowsData) {
      const updatedSelectedRows = updatedRows.filter(row =>
        props.rowsData?.some(sel => sel.contractSeqId === row.contractSeqId)
      );
      props.setRowsData(updatedSelectedRows);
    }
  };

  const gridOptions = {
    getRowId: (params) => {
      return `${params?.data?.contractSeqId}`
    },
    overlayLoadingTemplate: `<span class="ag--overlay-loading-center" >Please wait while data is loading</span>`,
    overlayNoRowsTemplate: `<span class="ag--overlay-no-rows-center" >No Data Available</span>`,
    defaultColDef: {
      sortable: true,
      filter: 'agMultiColumnFilter',
      autoHeight: true,
      cellStyle: {
        whiteSpace: "normal",
        wordBreak: "break-word",
        lineHeight: 1.5,
        display: "flex",
        alignItems: "center",
      },
      valueGetter: (params) => {
        if (!params.data) return "-";

        const field = params.colDef.field;
        const value = params.data[field];

        return value === null || value === undefined || value === ""
          ? "-"
          : value;
      },
    },
    onColumnMoved,
    onFilterChanged,
  }

  return (
    <>
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
        <div style={{ display: 'flex', gap: 10 }}>
          <IconButton onClick={handleClickHide}>
            <Badge badgeContent={badgeCount > 0 ? badgeCount : null} sx={{
              "& .MuiBadge-badge": {
                fontSize: "0.6rem", // Smaller font size
                height: "16px", // Adjust badge height
                minWidth: "16px", // Adjust badge width
                padding: "4px", // Adjust padding
                background: '#000',
                color: '#fff'
              },
            }}
            >
              <MoreVertIcon />
            </Badge>
          </IconButton>

          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleCloseHide}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            className="hidePopover"
            sx={{
              marginTop: 1,
              marginLeft: 0,
            }}
          >
            <div style={{ padding: 10, width: 150 }}>
              <p style={{ marginBottom: 10, marginTop: 5 }}>Hide Columns</p>
              <Select
                multiple
                value={selectedColumns}
                onChange={handleColumnChange}
                renderValue={renderDropdownValue}
                displayEmpty
                sx={{ backgroundColor: "#fff", border: '1px solid #000', width: '100%', height: 35, marginBottom: 5 }}
              >
                <MenuItem disabled value="">
                  <em>Select Columns</em>
                </MenuItem>
                <MenuItem
                  value="selectAll"
                  onClick={() => {
                    if (selectedColumns.length === columnFilterDropdownOption.length) {
                      setSelectedColumns([]);
                    } else {
                      setSelectedColumns(columnFilterDropdownOption.map(col => col.headerName));
                    }
                  }}
                >
                  <Checkbox
                    checked={selectedColumns.length === columnFilterDropdownOption.length}
                  />
                  <ListItemText primary="Select All" />
                </MenuItem>
                {columnFilterDropdownOption?.map(col => (
                  <MenuItem key={col.headerName} value={col.headerName}>
                    <Checkbox checked={selectedColumns?.includes(col.headerName)} />
                    <ListItemText primary={col.headerName} />
                  </MenuItem>
                ))}
              </Select>
              {stateSelector?.loading ?
                <Box style={{ textAlign: 'center' }}>
                  <CircularProgress color="inherit" style={{ width: '20px', height: '20px', textAlign: 'center' }} />
                </Box>
                :
                <ButtonComponent type="secondary" label="Save" onClick={handleSave} />
              }
            </div>
          </Popover>
          <FormControl sx={{ m: 1, minWidth: 200 }} size="small">
            <InputLabel>Jump to Column</InputLabel>
            <Select
              id="col-select"
              value={selectedColumn}
              label="Jump to Column"
              onChange={onColumnChange}
            >
              <MenuItem value="">Select Column</MenuItem>
              {headerItems?.flatMap(col =>
                col.children
                  ? col.children.map(child => (
                      <MenuItem key={child.field} value={child.field}>
                        {child.headerName || child.field}
                      </MenuItem>
                    ))
                  : [
                      <MenuItem key={col.field} value={col.field}>
                        {col.headerName || col.field}
                      </MenuItem>
                    ]
              )}
            </Select>
          </FormControl>
          <FormControl sx={{ m: 1, minWidth: 200 }} size="small">
            <InputLabel>PDT Master</InputLabel>
            <Select
              id="col-select"
              value={masterPdtSource}
              label="PDT Master"
              onChange={onPdtChange}
              disabled={props.rowsData?.length === 0}
            >
              <MenuItem value="">Select Option</MenuItem>
              <MenuItem value="CONTRACT_AUTOMATION">CONTRACT AUTOMATION</MenuItem>
              <MenuItem value="SAP">SAP</MenuItem>
              <MenuItem value="LT RECOMMENDED">LT RECOMMENDED</MenuItem>
              <MenuItem value="MANUAL PDT">MANUAL PDT</MenuItem>
            </Select>
          </FormControl>
        </div>
          <ButtonComponent
            maxWidth="150px"
            label="Clear All Filters"
            bgColor="black"
            color="white"
            disabled={disableFilterBtn}
            onClick={clearAllFilters}
          />
      </div>
      {(contractsDataList?.loading && !filteredColumnsData.length) ?
        <Loader />
        :
        <DataTablePagination
          height={750}
          columns={filteredColumnsData}
          rows={contractsDataList?.data?.contractDataOutputList || []}
          // enableCheckbox={false}
          onRowSelected={props.onRowSelected}
          suppressDragLeaveHidesColumns={false}
          selectionColumnDef={selectionColumnDef}
          rowSelection={props.rowSelection}
          onPageSizeChanged={props.onPageSizeChanged}
          goToPage={props.goToPage}
          pageSize={props.pageSize}
          currentPage={props.currentPage}
          totalPages={props.totalPages}
          totalPageItems={filteredTotalItems}
          totalItemCurrentPage={props.totalItemCurrentPage}
          onSelectionChanged={props.onSelectionChanged}
          gridOptions={gridOptions}
          getRowStyle={props.getRowStyle}
          onGridReady={onGridReady}
          postFilterChanged={handlePostFilterChanged}
          filteredTotalColumn={filteredTotalColumn}
          onCellValueChanged={props.handleCellValueChange}
        />
      }
    </>
  );
};

export default DataDetails;